/* eslint-disable @typescript-eslint/no-explicit-any */
import stringifyJSON from 'json-stable-stringify';
import type { FetchResponse, RequestParams, RestService } from '../rest-service';
import { DefaultRestService } from '../rest-service';
import type { BaseParams, BodyParams, ClassParams, MethodParams, ObjectParams, QueryParams } from '../utils';
import { buildRequestParams } from '../utils';
import { API } from './api';

type CallMethodParams = BaseParams &
  ClassParams &
  MethodParams &
  Partial<ObjectParams> &
  Partial<BodyParams> &
  Partial<QueryParams> & { method: 'GET' | 'POST' };

export interface FetchOptions {
  forcePost?: boolean;
  requestParams?: Partial<RequestParams>;
  queryParams?: Partial<QueryParams>;
}

export class CustomAPI extends API {
  /**
   * @param apiClassId - ID of the ONE class.
   * @param restService - Instance of the RestService class.
   */
  constructor(
    private apiClassId: string,
    restService: RestService = new DefaultRestService(),
  ) {
    super(restService);
  }

  /**
   * Performs an asynchronous request.
   * @param methodIdentifier - Method identifier.
   * @param body - Request body.
   * @param [options] - Additional options for the request.
   * @returns A Promise that resolves with the response of the request.
   */
  async fetch<T = unknown, K = unknown>(
    methodIdentifier: string,
    body: T,
    options?: FetchOptions,
  ): Promise<FetchResponse<K>> {
    // By default, all requests will be a GET request with _body as query param.
    // If _body excedes the maximum number of characters, a POST request will be made.
    const MAX_QUERY_STRING_LENGTH = 1024;
    const bodyLength = new URLSearchParams(stringifyJSON(body || {})).toString().length;
    let httpMethod: 'GET' | 'POST';
    if (options?.forcePost) {
      httpMethod = 'POST';
    } else {
      httpMethod = bodyLength < MAX_QUERY_STRING_LENGTH ? 'GET' : 'POST';
    }

    return this.callMethod({
      _classId: this.apiClassId,
      method: httpMethod,
      methodIdentifier,
      body,
      requestParams: options?.requestParams || {},
      queryParams: options?.queryParams,
    });
  }

  private callMethod(args: CallMethodParams): Promise<FetchResponse<any>> {
    if (args.method === 'GET') {
      const bodyAsParam = args.body ? encodeURIComponent(stringifyJSON(args.body)) : null;
      delete args.body;
      const requestParams = buildRequestParams({
        ...args,
        queryParams: {
          ...args.queryParams,
          ...(bodyAsParam ? { _body: bodyAsParam } : {}),
        },
      });
      return this.restService.get(requestParams);
    }

    const requestParams = buildRequestParams(args);
    return this.restService.post(requestParams);
  }
}
